import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  LayoutDashboard,
  Users,
  FileText,
  CreditCard,
  Landmark,
  RefreshCw,
  Download,
  Archive,
  UserCog,
  ClipboardList,
  LogOut,
  Menu,
  X,
  Globe,
  ChevronRight
} from 'lucide-react';

const navItems = [
  { key: 'dashboard', path: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'hr', 'viewer'] },
  { key: 'employees', path: '/employees', icon: Users, roles: ['admin', 'hr', 'viewer'] },
  { key: 'payroll', path: '/payroll', icon: FileText, roles: ['admin', 'hr', 'viewer'] },
  { key: 'advances', path: '/advances', icon: CreditCard, roles: ['admin', 'hr', 'viewer'] },
  { key: 'loans', path: '/loans', icon: Landmark, roles: ['admin', 'hr', 'viewer'] },
  { key: 'recurring', path: '/recurring', icon: RefreshCw, roles: ['admin', 'hr', 'viewer'] },
  { key: 'export', path: '/export', icon: Download, roles: ['admin', 'hr'] },
  { key: 'archive', path: '/archive', icon: Archive, roles: ['admin', 'hr', 'viewer'] },
  { key: 'users', path: '/users', icon: UserCog, roles: ['admin'] },
  { key: 'auditLog', path: '/audit-log', icon: ClipboardList, roles: ['admin'] },
];

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role)
  );

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-gray-200 shadow-sm
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <img 
                src="/logo.png" 
                alt="Kaufmanns Consulting" 
                className="h-12 object-contain"
              />
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-gray-500"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {filteredNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium
                    transition-colors duration-150
                    ${isActive 
                      ? 'bg-red-50 text-[#E31E24] border-l-2 border-[#E31E24]' 
                      : 'text-gray-600 hover:text-[#1A1A1A] hover:bg-gray-100'
                    }
                  `}
                  data-testid={`nav-${item.key}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{t(item.key)}</span>
                  <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
                </NavLink>
              ))}
            </nav>
          </ScrollArea>

          {/* User info & actions */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <span className="text-[#E31E24] font-bold text-sm">
                  {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A1A1A] truncate">
                  {user?.full_name}
                </p>
                <p className="text-xs text-gray-400 uppercase tracking-wider">
                  {t(user?.role)}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="flex-1 text-gray-600 hover:text-[#1A1A1A] hover:bg-gray-100"
                data-testid="sidebar-language-toggle"
              >
                <Globe className="w-4 h-4 mr-2" />
                {language.toUpperCase()}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="flex-1 text-gray-600 hover:text-[#E31E24] hover:bg-red-50"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden glass-header sticky top-0 z-30 px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className="text-gray-600"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </Button>
          <h1 className="text-lg font-bold text-[#1A1A1A]">
            <span className="text-[#E31E24]">K</span>AUFMANNS
          </h1>
          <div className="w-10" />
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
